import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import templateService from "../../../services/template.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
import {
    CALL_CONTENT_MAX_LENGTH,
    DEFAULT_CALL_LANGUAGE,
    DEFAULT_CALL_VOICE,
} from "../admin/callTemplateOptions";
import {
    AdminCallTemplateFields,
    AdminCallTemplateFooter,
    AdminCallTemplateMetaGrid,
    AdminCallTemplatePage,
} from "../admin/AdminCallTemplateForm";

const EditClinicCallTemplate = () => {
    const { clinicId, id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generatingAudio, setGeneratingAudio] = useState(false);
    const [template, setTemplate] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [formData, setFormData] = useState({
        content: "",
        voice: DEFAULT_CALL_VOICE,
        language: DEFAULT_CALL_LANGUAGE,
        status: true,
        reminderName: "",
        reminderTypeId: "",
    });
    const [originalData, setOriginalData] = useState(null);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);

    const fetchTemplateData = async () => {
        try {
            setLoading(true);

            const clinicResponse = await clinicService.getClinicById(clinicId);
            setClinic(clinicResponse.data);

            const templateResponse = await templateService.getCallTemplateById(clinicId, id);
            const templateData = templateResponse.data;
            setTemplate(templateData);
            setOriginalData(templateData);

            setFormData({
                content: templateData.content || "",
                voice: templateData.voice || DEFAULT_CALL_VOICE,
                language: templateData.language || DEFAULT_CALL_LANGUAGE,
                status: Boolean(templateData.status),
                reminderName: templateData.reminderName || "",
                reminderTypeId: templateData.reminderTypeId || "",
            });

            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }
            setIsPlaying(false);
        } catch (error) {
            console.error("Error fetching template data:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to load template data. Please try again.",
                confirmButtonColor: "#dc3545",
            }).then(() => {
                navigate("/clinic-templates");
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicId && id) {
            fetchTemplateData();
        }

        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicId, id]);

    const hasChanges = useMemo(() => {
        if (!originalData) return false;
        return (
            formData.content !== (originalData.content || "") ||
            formData.voice !== (originalData.voice || DEFAULT_CALL_VOICE) ||
            formData.language !== (originalData.language || DEFAULT_CALL_LANGUAGE) ||
            Boolean(formData.status) !== Boolean(originalData.status)
        );
    }, [formData, originalData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);

            await templateService.updateCallTemplates(clinicId, id, {
                id,
                content: formData.content,
                voice: formData.voice,
                language: formData.language,
                status: formData.status,
                reminderName: formData.reminderName,
                reminderTypeId: formData.reminderTypeId,
                clinicId,
            });

            await fetchTemplateData();

            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Call template updated successfully.",
                confirmButtonColor: "#0dcaf0",
                timer: 2000,
            });
        } catch (error) {
            console.error("Error updating template:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to update template. Please try again.",
                confirmButtonColor: "#dc3545",
            });
        } finally {
            setSaving(false);
        }
    };

    const clearCachedAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.removeAttribute("src");
            audioRef.current.load();
        }
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }
        setIsPlaying(false);
    };

    const handleGenerateAudio = async () => {
        try {
            setGeneratingAudio(true);
            clearCachedAudio();
            await templateService.generateAudio(clinicId, id);
            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Demo audio generated successfully. You can play it now.",
                confirmButtonColor: "#0dcaf0",
                timer: 2000,
            });
            await fetchTemplateData();
        } catch (error) {
            console.error("Error generating audio:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "Failed to generate audio. Please try again.",
                confirmButtonColor: "#dc3545",
            });
        } finally {
            setGeneratingAudio(false);
        }
    };

    const handlePlayAudio = async () => {
        try {
            if (audioUrl && audioRef.current) {
                await audioRef.current.play();
                setIsPlaying(true);
                return;
            }

            const response = await templateService.playAudio(clinicId, id);
            const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
            if (!audioBlob.size) {
                throw new Error("Empty audio response");
            }
            const url = URL.createObjectURL(audioBlob);
            setAudioUrl(url);

            if (audioRef.current) {
                audioRef.current.src = url;
                await audioRef.current.play();
                setIsPlaying(true);
                audioRef.current.onended = () => setIsPlaying(false);
                audioRef.current.onerror = () => {
                    setIsPlaying(false);
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "Failed to play audio. Please try again.",
                        confirmButtonColor: "#dc3545",
                    });
                };
            }
        } catch (error) {
            console.error("Error playing audio:", error);
            setIsPlaying(false);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "Failed to play audio. Generate demo audio again, then try play.",
                confirmButtonColor: "#dc3545",
            });
        }
    };

    const handlePauseAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleCancel = () => {
        if (!hasChanges) {
            navigate("/clinic-templates");
            return;
        }
        Swal.fire({
            title: "Unsaved changes",
            text: "Discard your edits and leave this page?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, discard",
            cancelButtonText: "Keep editing",
        }).then((result) => {
            if (result.isConfirmed) {
                navigate("/clinic-templates");
            }
        });
    };

    if (loading) {
        return (
            <AdminCallTemplatePage
                title="Edit Call Template"
                subtitle="Clinic call script, Twilio voice, and Polly demo audio"
                backTo="/clinic-templates"
                backLabel="Back to clinic templates"
            >
                <Loading />
            </AdminCallTemplatePage>
        );
    }

    return (
        <AdminCallTemplatePage
            title="Edit Call Template"
            subtitle={
                clinic
                    ? `${clinic.name}${clinic.city ? ` · ${clinic.city}` : ""}`
                    : "Clinic call script, Twilio voice, and Polly demo audio"
            }
            backTo="/clinic-templates"
            backLabel="Back to clinic templates"
            headerBadge={
                hasChanges ? (
                    <span className="badge bg-warning text-dark">
                        <i className="bi bi-pencil me-1"></i>
                        Unsaved changes
                    </span>
                ) : (
                    <span className="badge bg-light text-muted border">Saved</span>
                )
            }
        >
            <audio ref={audioRef} style={{ display: "none" }} />

            <form onSubmit={handleSubmit}>
                <AdminCallTemplateMetaGrid
                    items={[
                        { label: "Clinic", value: clinic?.name || "—" },
                        { label: "Reminder", value: formData.reminderName || "—" },
                        { label: "Created", value: template?.createdAt || "—" },
                        { label: "Modified", value: template?.modifiedAt || "—" },
                        { label: "Template ID", value: template?.id, mono: true },
                        {
                            label: "Reminder type ID",
                            value: template?.reminderTypeId || "—",
                            mono: true,
                        },
                    ]}
                />

                <AdminCallTemplateFields
                    formData={formData}
                    onChange={handleChange}
                    contentField="content"
                    hideUsedBy
                    reminderNameControl={
                        <>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.reminderName}
                                readOnly
                                disabled
                            />
                            <div className="form-text">Reminder type cannot be changed</div>
                        </>
                    }
                />

                <section className="admin-call-template-section mt-4 mb-4">
                    <h6 className="admin-call-template-section__title">
                        <i className="bi bi-toggle-on me-2"></i>
                        Status
                    </h6>
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="callTemplateStatus"
                            name="status"
                            checked={formData.status}
                            onChange={handleChange}
                            style={{ width: "2.75em", height: "1.4em" }}
                        />
                        <label className="form-check-label ms-2" htmlFor="callTemplateStatus">
                            {formData.status ? "Active — used for automated calls" : "Inactive"}
                        </label>
                    </div>
                </section>

                <section className="admin-call-template-section mb-4">
                    <h6 className="admin-call-template-section__title">
                        <i className="bi bi-music-note-beamed me-2"></i>
                        Demo audio (Polly preview)
                    </h6>
                    <div className="admin-call-template-demo">
                        {template?.audioPath ? (
                            <>
                                <p className="small text-muted mb-3">
                                    Preview uses Amazon Polly mapped from the selected Twilio voice.
                                    Live calls use Twilio &lt;Say&gt;, not this MP3.
                                </p>
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                    {!isPlaying ? (
                                        <button
                                            type="button"
                                            className="btn btn-info btn-sm text-white"
                                            onClick={handlePlayAudio}
                                            disabled={generatingAudio || saving}
                                        >
                                            <i className="bi bi-play-fill me-1"></i>
                                            Play demo
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-warning btn-sm"
                                            onClick={handlePauseAudio}
                                        >
                                            <i className="bi bi-pause-fill me-1"></i>
                                            Pause
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleGenerateAudio}
                                        disabled={generatingAudio || isPlaying || saving}
                                    >
                                        {generatingAudio ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Regenerating…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-arrow-repeat me-1"></i>
                                                Regenerate demo
                                            </>
                                        )}
                                    </button>
                                </div>
                                {isPlaying && (
                                    <div className="progress mb-2" style={{ height: "4px" }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                )}
                                <small className="text-muted font-monospace">
                                    {template.audioPath.split("/").pop()}
                                </small>
                            </>
                        ) : (
                            <div className="text-center py-3">
                                <i className="bi bi-file-earmark-music display-6 text-muted"></i>
                                <p className="mt-2 mb-3 text-muted">
                                    No demo audio yet. Generate a Polly preview of this script.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-info btn-sm text-white"
                                    onClick={handleGenerateAudio}
                                    disabled={generatingAudio || !formData.content?.trim() || saving}
                                >
                                    {generatingAudio ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Generating…
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-play-circle me-1"></i>
                                            Generate demo audio
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="form-text mt-2">
                        Script length {formData.content.length}/{CALL_CONTENT_MAX_LENGTH}. Save
                        voice/script changes before regenerating demo audio.
                    </div>
                </section>

                <div className="pt-3 border-top">
                    <AdminCallTemplateFooter
                        onCancel={handleCancel}
                        onSubmitLabel="Save call template"
                        submitting={saving}
                        submitDisabled={!hasChanges}
                        submitVariant="info"
                    />
                </div>
            </form>
        </AdminCallTemplatePage>
    );
};

export default EditClinicCallTemplate;
